import {
  invokeTagManagerConsentUpdateScript,
  pushCookieConsentUpdateEvent,
} from 'client/shared/helpers/vendor-trackers-helper'
import { isEUCountry } from 'shared/tools/country-helpers'
import { getDomain } from 'shared/tools/url-helper'

export type CookieConsent = {
  cookieBasic: boolean
  cookiePerformance: boolean
  cookieMarketing: boolean
}

export const COOKIES_FULL_CONSENT = {
  cookieBasic: true,
  cookiePerformance: true,
  cookieMarketing: true,
}

const COOKIES_BASIC_CONSENT = {
  cookieBasic: true,
  cookiePerformance: false,
  cookieMarketing: false,
}

/** Cookie domain: root domain (.bookmate.com) so consent is shared across all subdomains (locales). */
function getCookieDomain(): string | null {
  if (typeof window === 'undefined') return null
  const h = window.location.hostname
  if (!h || h === 'localhost' || h === '127.0.0.1' || !h.includes('.'))
    return null
  // Do not set domain for IP addresses (browsers do not accept domain=.x.x.x.x)
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(h) || !/[a-zA-Z]/.test(h)) return null
  return `.${getDomain(h)}`
}

export function setCookie(cookieName: string, value: unknown): void {
  if (typeof document === 'undefined') return
  const domain = getCookieDomain()
  const domainPart = domain ? `;domain=${domain}` : ''
  document.cookie = `${cookieName}=${JSON.stringify(
    value,
  )};path=/;max-age=${cookieLifeTime}${domainPart}`
}

function getCookie(name: string): string | null {
  if (typeof document !== 'object') return null
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')
  const matches = document.cookie.match(
    new RegExp(`(?:^|; )${escaped}=([^;]*)`),
  )
  if (!matches) return null
  try {
    return decodeURIComponent(matches[1])
  } catch {
    return null
  }
}

function parseJsonCookie<T>(value: string | null): T | null {
  if (value == null) return null
  try {
    return JSON.parse(value) as T
  } catch {
    return null
  }
}

function isValidConsentShape(obj: unknown): obj is CookieConsent {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'cookieBasic' in obj &&
    'cookiePerformance' in obj &&
    'cookieMarketing' in obj &&
    typeof (obj as CookieConsent).cookieBasic === 'boolean' &&
    typeof (obj as CookieConsent).cookiePerformance === 'boolean' &&
    typeof (obj as CookieConsent).cookieMarketing === 'boolean'
  )
}

export const cookieLifeTime = 1000 * 60 * 60 * 24

const SOFT_POLICY_PATHNAMES = [] as string[]

export default {
  getCurrentCookieConsent(): CookieConsent | null {
    const raw = getCookie('bm_cookie_consent')
    const parsed = parseJsonCookie<CookieConsent>(raw)
    return isValidConsentShape(parsed) ? parsed : null
  },

  isSoftCookieLocation(pathname: string): boolean {
    return SOFT_POLICY_PATHNAMES.some(path => path === pathname)
  },

  isSoftCookieCountry(countryName: string): boolean {
    return !isEUCountry(countryName)
  },

  isSoftCookiePolicy(pathname: string, countryName: string): boolean {
    return (
      this.isSoftCookieLocation(pathname) ||
      this.isSoftCookieCountry(countryName)
    )
  },

  isConsentGiven(): boolean {
    const raw = getCookie('bm_cookie_consent_given')
    const value = parseJsonCookie<boolean>(raw)
    return value === true
  },

  isConsentSet(): boolean {
    const consent = this.getCurrentCookieConsent()
    return Boolean(consent && Object.keys(consent).length > 0)
  },

  isFullConsent(): boolean {
    const consent = this.getCurrentCookieConsent()
    if (!consent) return false
    return !Object.values(consent).some(v => v === false)
  },

  isStashedConsent(): boolean {
    const raw = getCookie('bm_cookie_consent_stash')
    const data = parseJsonCookie<CookieConsent>(raw)
    return isValidConsentShape(data)
  },

  enableAllCategories({
    force,
    country,
  }: {
    force?: boolean
    country?: string
  }): void {
    if (force) {
      if (this.isConsentSet()) {
        this.stashCurrentConsent()
      }
    } else {
      invokeTagManagerConsentUpdateScript(true, true)
      if (isEUCountry(country as string)) pushCookieConsentUpdateEvent()
    }
    this.setFullConsent()
  },

  setUserChoice(choice: CookieConsent): void {
    setCookie('bm_cookie_consent_choice', { choice })
  },

  setFullConsent(): void {
    setCookie('bm_cookie_consent', { ...COOKIES_FULL_CONSENT })
  },

  setBasicConsent({
    mode,
    country,
  }: {
    country?: string
    mode?: string
  }): void {
    setCookie('bm_cookie_consent', { ...COOKIES_BASIC_CONSENT })

    if (mode) {
      invokeTagManagerConsentUpdateScript(false, false)
      if (isEUCountry(country as string)) pushCookieConsentUpdateEvent()
    }
  },

  setCustomConsent(consent: CookieConsent, country: string): void {
    setCookie('bm_cookie_consent', { ...consent })

    invokeTagManagerConsentUpdateScript(
      consent.cookieMarketing,
      consent.cookiePerformance,
    )
    if (isEUCountry(country as string)) pushCookieConsentUpdateEvent()
  },

  setConsentGiven(): void {
    setCookie('bm_cookie_consent_given', true)
  },

  stashCurrentConsent(): void {
    const consent = this.getCurrentCookieConsent()
    if (consent) {
      setCookie('bm_cookie_consent_stash', { ...consent })
    }
  },

  applyConsentStash(country: string): void {
    const raw = getCookie('bm_cookie_consent_stash')
    const stash = parseJsonCookie<CookieConsent>(raw)
    if (!isValidConsentShape(stash)) {
      this.clearConsentStash()
      return
    }

    setCookie('bm_cookie_consent', { ...stash })
    invokeTagManagerConsentUpdateScript(
      stash.cookieMarketing,
      stash.cookiePerformance,
    )
    if (isEUCountry(country)) pushCookieConsentUpdateEvent()
    this.clearConsentStash()
  },

  clearConsentStash(): void {
    if (typeof document === 'undefined') return
    const domain = getCookieDomain()
    const domainPart = domain ? `;domain=${domain}` : ''
    document.cookie = `bm_cookie_consent_stash=;path=/;max-age=0${domainPart}`
  },
}
